import { useState } from 'react';
import { api } from '../../lib/api';
import { Pill } from '../../components/ui';

// 序列号独立模块（T45 前置）
// 查询 / 合同归属 / 溯源入口；同号多件候选并列，禁止合并；无序列号走内部编号

type SerialCandidate = {
  id: string;
  customer_serial?: string;
  internal_code?: string;
  asset_type?: string;
  customer_code?: string;
  customer_name?: string;
  item_code?: string;
  item_name_zh?: string;
  status?: string;
  current_version?: number;
  has_serial?: boolean;
  identity?: string;
  contracts?: { id: string; number: string; status?: string; contract_date?: string }[];
  primary_contract?: { number: string } | null;
  service_cycles?: { version: number; status?: string; intake_date?: string }[];
  latest_delivery?: { number: string; status?: string } | null;
  trace_url?: string;
};

type LegacySerialCandidate = {
  id: string; customer_serial: string; item_code?: string | null; source_status?: string | null;
  purchase_document_no?: string | null; purchase_date?: string | null;
  delivery_document_no?: string | null; delivery_date?: string | null;
  lifecycle_status: string; linked_asset_id?: string | null;
};

type LegacyInspection = {
  id: string; number?: string | null; serial_number: string; inspection_date?: string | null;
  process_type?: string | null; result?: string | null; source_system?: string | null;
  work_order_number?: string | null; contract_number?: string | null; delivery_number?: string | null;
  item_code?: string | null; remarks_zh?: string | null;
};

type SerialQueryResult = {
  query: Record<string, string | null>;
  match_count: number;
  needs_resolution: boolean;
  merged: boolean;
  candidates: SerialCandidate[];
  legacy_candidates?: LegacySerialCandidate[];
  legacy_inspections?: LegacyInspection[];
  total_match_count?: number;
};

export function SerialLookupPage() {
  const [q, setQ] = useState('');
  const [internalCode, setInternalCode] = useState('');
  const [data, setData] = useState<SerialQueryResult | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [traceAsset, setTraceAsset] = useState<string | null>(null);
  const [trace, setTrace] = useState<any>(null);

  const search = async () => {
    setLoading(true);
    setError('');
    setData(null);
    setTrace(null);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('serial', q.trim());
      if (internalCode.trim()) params.set('internal_code', internalCode.trim());
      if (!q.trim() && !internalCode.trim()) {
        setError('请输入序列号或内部工件编号');
        setLoading(false);
        return;
      }
      const res = await api.get<{ data: SerialQueryResult }>(`/api/serials?${params}`);
      setData(res.data);
      if (!res.data?.match_count && !res.data?.legacy_candidates?.length && !res.data?.legacy_inspections?.length) setError('新系统工件台账和 ERPNext 历史序列号中都未找到匹配记录');
    } catch (e: any) {
      setError(e?.message || '查询失败');
    }
    setLoading(false);
  };

  const openTrace = async (id: string) => {
    setTraceAsset(id);
    try {
      const res = await api.get<{ data: any }>(`/api/physical-assets/${id}/trace`);
      setTrace(res.data);
    } catch {
      setTrace(null);
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 960 }}>
      <h2 style={{ margin: '0 0 4px' }}>序列号查询</h2>
      <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 16px' }}>
        一次查询新系统工件和 ERPNext 承袭的历史序列号。两类来源分开展示，同号记录不自动合并；新系统工件显示完整维修溯源，历史记录显示 ERPNext 原始单据，有实际回链时可打开关联溯源。
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="序列号，如 TRACE-77"
          style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, minWidth: 200 }}
          onKeyDown={(e) => e.key === 'Enter' && search()}
        />
        <input
          value={internalCode}
          onChange={(e) => setInternalCode(e.target.value)}
          placeholder="内部工件编号 / asset id"
          style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, minWidth: 200 }}
          onKeyDown={(e) => e.key === 'Enter' && search()}
        />
        <button
          type="button"
          onClick={search}
          disabled={loading}
          style={{ padding: '8px 16px', borderRadius: 8, border: 0, background: '#0d3b66', color: '#fff', cursor: 'pointer' }}
        >
          {loading ? '查询中…' : '查询'}
        </button>
      </div>

      {error && <div style={{ color: '#b91c1c', marginBottom: 12, fontSize: 13 }}>{error}</div>}

      {data && (data.match_count > 0 || Boolean(data.legacy_candidates?.length) || Boolean(data.legacy_inspections?.length)) && (
        <>
          <div style={{ marginBottom: 8, fontSize: 13, color: '#475569' }}>
            命中新系统工件 <b>{data.match_count}</b> 件，ERPNext 历史序列号 <b>{data.legacy_candidates?.length || 0}</b> 条，历史质检记录 <b>{data.legacy_inspections?.length || 0}</b> 条
            {data.needs_resolution && (
              <span style={{ marginLeft: 8 }}>
                <Pill tone="warn" label="同号多件 · 请人工判定，禁止合并" />
              </span>
            )}
          </div>
          {data.candidates.map((c) => (
            <div
              key={c.id}
              style={{
                border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, marginBottom: 10, background: '#fff',
              }}
            >
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                <strong>{c.customer_serial || '(无序列号)'}</strong>
                <span style={{ color: '#64748b', fontSize: 12 }}>身份 {c.id.slice(0, 8)}…</span>
                {c.status && <Pill tone="info" label={c.status} />}
                {c.current_version != null && <Pill tone="ok" label={`V${c.current_version}`} />}
                {!c.has_serial && <Pill tone="warn" label="内部编号" />}
              </div>
              <div style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.7 }}>
                <div>客户：{c.customer_name || '-'}（{c.customer_code || '-'}）</div>
                <div>物料：{c.item_name_zh || '-'}（{c.item_code || '-'}）</div>
                <div>
                  合同归属：
                  {c.contracts?.length
                    ? c.contracts.map((x) => x.number).join('、')
                    : '（暂无强关联合同）'}
                </div>
                <div>
                  维修周期：
                  {c.service_cycles?.length
                    ? c.service_cycles.map((v) => `V${v.version}(${v.status || '-'})`).join('、')
                    : '-'}
                </div>
                <div>最近交货：{c.latest_delivery ? `${c.latest_delivery.number} · ${c.latest_delivery.status}` : '-'}</div>
              </div>
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => openTrace(c.id)}
                  style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', cursor: 'pointer', fontSize: 12 }}
                >
                  查看溯源
                </button>
              </div>
            </div>
          ))}
          {(data.legacy_candidates || []).map((c) => (
            <div key={`legacy:${c.id}`} style={{ border: '1px solid #cbd5e1', borderRadius: 10, padding: 12, marginBottom: 10, background: '#f8fafc' }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                <strong>{c.customer_serial}</strong>
                <Pill tone={c.lifecycle_status.startsWith('本月') ? 'warn' : 'ok'} label={c.lifecycle_status} />
                <Pill tone="neutral" label="ERPNext 历史记录" />
              </div>
              <div style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.7 }}>
                <div>物料：{c.item_code || '—'} · ERPNext 状态：{c.source_status || '—'}</div>
                <div>源端交货单：{c.delivery_document_no || '—'} · 日期：{c.delivery_date || '—'}</div>
                <div>源端采购单：{c.purchase_document_no || '—'} · 日期：{c.purchase_date || '—'}</div>
                <div>{c.linked_asset_id ? '已回链新系统工件，可查看下方新系统溯源。' : '尚未回链新系统工件；这里显示 ERPNext 承袭记录，未导入的维修周期不作推断。'}</div>
              </div>
              {c.linked_asset_id ? <button type="button" onClick={() => openTrace(c.linked_asset_id!)} style={{ marginTop: 8 }}>查看关联工件溯源</button> : null}
            </div>
          ))}
          {(data.legacy_inspections || []).length > 0 && (
            <div style={{ border: '1px solid #f3d08a', borderRadius: 10, padding: 12, marginBottom: 10, background: '#fffaf0' }}>
              <strong>ERPNext 历史维修 / 质检记录（按序列号匹配）</strong>
              <div style={{ fontSize: 12, color: '#856404', margin: '4px 0 8px' }}>展示已承接的工卡 / QC 记录；它们与新系统维修周期分开保存，不自动合并。</div>
              {(data.legacy_inspections || []).map((r) => (
                <div key={r.id} style={{ padding: '7px 0', borderTop: '1px solid #f1e5c7', fontSize: 12.5, lineHeight: 1.7 }}>
                  <b>{r.inspection_date || '无日期'} · {r.process_type || '检验'} · {r.result || '—'}</b>
                  <div>工单：{r.work_order_number || '—'} · 合同：{r.contract_number || '—'} · 交货：{r.delivery_number || '—'} · 物料：{r.item_code || '—'} · 来源：{r.source_system || 'ERPNext'}</div>
                  {r.remarks_zh ? <div>备注：{r.remarks_zh}</div> : null}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {traceAsset && (
        <div style={{ marginTop: 16, border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#f8fafc' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>溯源 · {traceAsset.slice(0, 8)}…</h3>
          {!trace && <div style={{ color: '#64748b', fontSize: 12 }}>加载中…</div>}
          {trace && (
            <div style={{ fontSize: 12.5 }}>
              {(trace.cycles || trace.history || []).map((cy: any) => (
                <div key={cy.version} style={{ marginBottom: 10 }}>
                  <strong>V{cy.version}</strong> {cy.status || ''} {cy.intake_date || ''}
                  <ul style={{ margin: '4px 0 0 18px' }}>
                    {(cy.events || []).slice(0, 12).map((ev: any, i: number) => (
                      <li key={i}>{ev.date || ''} · {ev.label}{ev.detail ? ` — ${ev.detail}` : ''}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
